import { TableRow } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

// COMPONENTS
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import KOGCollapse from '@/main/components/shared/kogCollapse/kogCollapse';
import {
  RowHeaderTextCenter,
  SkeletonLoading,
  SpinnerLoading
} from '@/main/components/shared/styles';

// SERVICES / REDUX
import {
  QuoteHeaderAnswerInformationResponse,
  QuoteSupplierItemPartResponse
} from '@/main/models/response';
import { StatusQuoteSupplierItem } from '@/main/services/enum/StatusQuoteSupplierItem';
import { getTranslateByKey } from '@/main/services/utils';
import { useWarehouseReducer } from '@/main/store/warehouse';

import KogCheckbox from '@/main/components/shared/checkbox/kogCheckbox';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ICreateAnswerForm } from '../interfaceCreateAnswer';
import TableRowAnswer from './TableRowAnswer/TableRowAnswer';
import { buildSizeColumns } from './columnConfig';
import * as S from './styles';
import { isFinishedTimeWithAnswered } from '@/main/screens/quotation/createAnswer/answeredParts/utilscreateAnswer';
interface ICardGridAnswerProps {
  dataGrid: QuoteSupplierItemPartResponse[];
  dataHeader: QuoteHeaderAnswerInformationResponse;
  loading?: boolean;
  hasReadOnly?: boolean;
  idStatus: number;
  openModalOnce: boolean;
  isDeliveryBySupplier: boolean;
  isFinishedTime: boolean;
  setOpenModalOnce: React.Dispatch<React.SetStateAction<boolean>>;
  loadGrid: (
    guidQuote: string,
    guidSupplier: string,
    idQuoteSuppItemStatus: number | null
  ) => void;
  handleOpenModalDetails: (guidPart: string) => void;
  updateDataGrid: (data: QuoteSupplierItemPartResponse[]) => void;
  isRequoteNational?: boolean;
  isOpenInitial?: boolean;
  isShowModalReasonAll: boolean;
  setIsShowModalReasonAll: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModalImages: (item: QuoteSupplierItemPartResponse, allowEditing: boolean) => void;
}
const TitleHeader = ({
  title,
  notRequired = false,
  isTextCenter = false,
  subtitle = null
}: {
  title: string | React.ReactNode;
  notRequired?: boolean;
  isTextCenter?: boolean;
  subtitle?: string | null;
}) => (
  <S.RowTitleHeader>
    {isTextCenter ? (
      <RowHeaderTextCenter>
        {title}
        {!notRequired && <S.TextAsterisk> *</S.TextAsterisk>}
        <p>{subtitle}</p>
      </RowHeaderTextCenter>
    ) : (
      <S.ContainerTitle>
        <p>
          {title} {!notRequired && <S.TextAsterisk> *</S.TextAsterisk>}
        </p>
        <p>{subtitle}</p>
      </S.ContainerTitle>
    )}
  </S.RowTitleHeader>
);
function GridAnswer({
  dataGrid,
  loading = false,
  hasReadOnly = false,
  openModalOnce,
  dataHeader,
  loadGrid,
  setOpenModalOnce,
  isShowModalReasonAll,
  setIsShowModalReasonAll,
  handleOpenModalDetails,
  isDeliveryBySupplier = true,
  isFinishedTime,
  handleOpenModalImages
}: ICardGridAnswerProps) {
  const { control, setValue, getValues } = useFormContext<ICreateAnswerForm>();
  const { fields } = useFieldArray({ control, name: 'answeredItems' });

  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [havePartInventory, setHavePartInventory] = useState<boolean>(false);

  const { warehouse: warehouseReducer } = useWarehouseReducer();

  const haveOnlyOneWarehouse = warehouseReducer?.data?.length === 1;
  const isShowIconEdit = hasReadOnly && !isFinishedTime && !(dataHeader && isFinishedTimeWithAnswered(dataHeader));
  const sizeColumns = useMemo(
    () =>
      buildSizeColumns({
        haveOnlyOneWarehouse,
        isDeliveryBySupplier,
        havePartInventory,
        hasCollumnAction: isShowIconEdit
      }),
    [haveOnlyOneWarehouse, isDeliveryBySupplier, havePartInventory, isShowIconEdit]
  );

  const hasActionColumn = havePartInventory || isShowIconEdit;

  function handleToggleSelectAll() {
    const answeredItems = getValues('answeredItems');

    if (allSelected) {
      answeredItems.forEach((item, index) => {
        if (item.notQuote) {
          setValue(`answeredItems.${index}.notQuote`, false);
          setValue(`answeredItems.${index}.reason`, null);
          setValue(`answeredItems.${index}.observation`, '');
        }
      });
      setAllSelected(false);
      return;
    }
    answeredItems.forEach((item, index) => {
      if (!item.notQuote && item.isPendingAnswer) {
        setValue(`answeredItems.${index}.notQuote`, true);
      }
    });
    setAllSelected(true);
  }

  function getMaximumLengthHeader(string: string, maxchars: number): string {
    return string.length > maxchars + 1
      ? `${string.substring(0, maxchars)}.`
      : string;
  }

  const rowsHeader = [
    <TitleHeader title={getTranslateByKey('information.requestedPart')} />,
    <TitleHeader
      title={getTranslateByKey('information.partNumber')}
      notRequired
    />,
    <TitleHeader title={getTranslateByKey('information.partTypes')} />,
    !haveOnlyOneWarehouse && (
      <TitleHeader title={getTranslateByKey('information.warehouse')} />
    ),
    <TitleHeader title={getTranslateByKey('information.sku')} notRequired />,
    <TitleHeader title={getTranslateByKey('information.brand')} notRequired />,
    <TitleHeader title={getTranslateByKey('information.amount')} />,
    <TitleHeader title={getTranslateByKey('information.price')} />,
    <TitleHeader title={getTranslateByKey('information.pickupDaysDeadline')} />,
    !isDeliveryBySupplier && (
      <TitleHeader
        title={`${getTranslateByKey('partDetail.dimensions')}`}
        subtitle={`${getMaximumLengthHeader(
          getTranslateByKey('information.height'),
          4
        )} x ${getMaximumLengthHeader(
          getTranslateByKey('information.width'),
          4
        )} x ${getMaximumLengthHeader(
          getTranslateByKey('information.depth'),
          4
        )}`}
      />
    ),
    !isDeliveryBySupplier && (
      <TitleHeader title={`${getTranslateByKey('information.weight')} (kg)`} />
    ),
    <S.TitleHeaderCheckbox>
      {getTranslateByKey('information.notQuote')}
      {!hasReadOnly && (
        <KogCheckbox
          disabled={hasReadOnly}
          key="checkbox"
          checked={allSelected}
          value={allSelected}
          onChange={handleToggleSelectAll}
          id="id-checkbox-selectAll"
        />
      )}
    </S.TitleHeaderCheckbox>,
    hasActionColumn ? <TitleHeader title="" notRequired /> : false
  ].filter(Boolean);

  function checkAllNotQuoteTrue(data: QuoteSupplierItemPartResponse[]) {
    return data.every(item => item.notQuote);
  }

  useEffect(() => {
    if (dataGrid && dataGrid.length != 0 && checkAllNotQuoteTrue(dataGrid)) {
      setAllSelected(true);
    }
  }, [dataGrid]);

  if (!loading && dataGrid.length === 0) {
    return <ResultNotFoundComponent />;
  }

  return (
    <S.ContainerGrid>
      <BoxGrid
        rowHeaders={rowsHeader}
        typeGrid="backgroundBody"
        sizeColumns={sizeColumns}>
        {loading
          ? Array.from(new Array(4)).map((_, index) => (
            <TableRow key={index}>
              {rowsHeader.map((_, index) => (
                <S.TableCellGrid key={index} $showMobile>
                  <SkeletonLoading />
                </S.TableCellGrid>
              ))}
            </TableRow>
          ))
          : dataGrid.map((row, index) => (
            <TableRowAnswer
              key={fields[index]?.id ?? row.idUnique}
              row={row}
              fieldArrayName="answeredItems"
              rowIndex={index}
              dataHeader={dataHeader}
              hasReadOnly={hasReadOnly}
              isFinishedTime={isFinishedTime}
              isShowModalReasonAll={isShowModalReasonAll}
              setIsShowModalReasonAll={setIsShowModalReasonAll}
              handleOpenModalDetails={handleOpenModalDetails}
              setAllSelected={setAllSelected}
              allSelected={allSelected}
              loadGrid={loadGrid}
              setHavePartInventory={setHavePartInventory}
              handleOpenModalImages={handleOpenModalImages}
              singleActionColumn={hasActionColumn}
            />
          ))}
      </BoxGrid>
    </S.ContainerGrid>
  );
}
function CardGridAnswer(props: ICardGridAnswerProps) {
  const title =
    props.idStatus === StatusQuoteSupplierItem.Answered
      ? getTranslateByKey('information.partsAnswered')
      : getTranslateByKey('information.partsPending');
  const itemCount = props.dataGrid.length;

  return (
    <S.Container>
      <KOGCollapse
        isOpenCollapseinitial={props.isOpenInitial ?? true}
        showBorder
        keepMounted
        header={
          <S.AreaTitleCard>
            <S.TitleCard>{title}</S.TitleCard>
            {props.loading && <SpinnerLoading size="xsmall" noMargin />}
          </S.AreaTitleCard>
        }
        content={<GridAnswer {...props} />}
      />
    </S.Container>
  );
}

export default CardGridAnswer;
